import React, { useEffect, useState } from "react";
import { Loader2, Trash2, Mail, Phone, Building2, MapPin, Calendar, RefreshCw, CheckCheck } from "lucide-react";
import api from "../../lib/api";
import { useToast } from "../../hooks/use-toast";

const FIELDS = [
  { key: "company", label: "Company", icon: Building2 },
  { key: "position", label: "Position" },
  { key: "website", label: "Website / Social" },
  { key: "employees", label: "Employees / Foot traffic" },
  { key: "people", label: "People on premises" },
  { key: "address", label: "Site address", icon: MapPin },
  { key: "visitDate", label: "Preferred date" },
  { key: "visitTime", label: "Preferred time" },
  { key: "placement", label: "Placement" },
  { key: "notes", label: "Notes" },
];

export default function SubmissionsPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const { toast } = useToast();

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/submissions");
      setItems(res.data);
    } catch (e) {
      toast({ title: "Couldn't load submissions", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this submission?")) return;
    await api.delete(`/admin/submissions/${id}`);
    setItems((x) => x.filter((i) => i.id !== id));
    toast({ title: "Submission deleted" });
  };

  const toggleOpen = async (item) => {
    const next = openId === item.id ? null : item.id;
    setOpenId(next);
    if (next && !item.read) {
      try {
        await api.patch(`/admin/submissions/${item.id}/read`);
        setItems((x) => x.map((i) => (i.id === item.id ? { ...i, read: true } : i)));
      } catch (e) { /* ignore */ }
    }
  };

  const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  };

  if (loading) {
    return <div className="py-20 grid place-items-center"><Loader2 className="animate-spin text-gold-dark" size={32} /></div>;
  }

  const unread = items.filter((i) => !i.read).length;

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="font-display text-2xl text-ink">Form Submissions</h2>
          <p className="text-sm text-muted-foreground">{items.length} total · {unread} unread</p>
        </div>
        <button onClick={load} className="flex items-center gap-2 rounded-full bg-gold-soft hover:bg-gold text-ink px-4 h-10 text-sm font-semibold transition-colors">
          <RefreshCw size={15} /> Refresh
        </button>
      </div>

      {items.length === 0 ? (
        <div className="py-20 text-center text-muted-foreground">No submissions yet.</div>
      ) : (
        <div className="space-y-3">
          {items.map((item) => (
            <div key={item.id} className={`rounded-xl border transition-colors ${item.read ? "border-gold/15" : "border-gold bg-gold-soft/30"}`}>
              <button onClick={() => toggleOpen(item)} className="w-full text-left p-4 flex items-center gap-4">
                {!item.read && <span className="h-2.5 w-2.5 rounded-full bg-gold shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-ink">{item.fullName}</span>
                    <span className="rounded-full bg-ink text-gold text-[11px] px-2 py-0.5 uppercase tracking-wider">{item.source}</span>
                  </div>
                  <div className="mt-1 flex items-center gap-4 text-sm text-muted-foreground flex-wrap">
                    <span className="flex items-center gap-1"><Mail size={13} /> {item.email}</span>
                    <span className="flex items-center gap-1"><Phone size={13} /> {item.phone}</span>
                    <span className="flex items-center gap-1"><Calendar size={13} /> {fmtDate(item.created_at)}</span>
                  </div>
                </div>
                <Trash2 size={16} className="text-muted-foreground hover:text-red-500 shrink-0" onClick={(e) => { e.stopPropagation(); remove(item.id); }} />
              </button>
              {openId === item.id && (
                <div className="px-4 pb-4 pt-1 border-t border-gold/15">
                  <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 mt-3">
                    {FIELDS.filter((f) => item[f.key]).map((f) => (
                      <div key={f.key} className="flex justify-between gap-4 py-1 border-b border-gold/10">
                        <dt className="text-sm text-muted-foreground">{f.label}</dt>
                        <dd className="text-sm font-medium text-ink text-right">{item[f.key]}</dd>
                      </div>
                    ))}
                  </dl>
                  <a href={`mailto:${item.email}`} className="mt-4 inline-flex items-center gap-2 rounded-full bg-gold px-5 h-10 text-sm font-semibold text-ink hover:bg-gold-dark transition-colors">
                    <Mail size={15} /> Reply by email
                  </a>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
